/**
 * Sprites — every piece of art in the game, drawn once as vector-style canvas
 * paths and baked into offscreen canvases at 2x resolution. The render loop
 * only ever blits these, so art quality costs nothing per frame.
 *
 * Conventions:
 *  - every sprite gets a soft dark outline (#31343c-ish) for cartoon cohesion
 *  - light comes from the top-left: top edges highlighted, bottoms shaded
 *  - cars face DOWN (they drive top → bottom); the chicken faces RIGHT
 */
import { LANE_W, VIEW_H, SIDEWALK_W } from './Layout.js';

const SS = 3; // supersample factor — headroom for hi-DPI screens

export const OUTLINE = '#31343c';

function bake(w, h, draw) {
  const c = document.createElement('canvas');
  c.width = Math.ceil(w * SS);
  c.height = Math.ceil(h * SS);
  const ctx = c.getContext('2d');
  ctx.scale(SS, SS);
  ctx.lineJoin = 'round';
  ctx.lineCap = 'round';
  draw(ctx, w, h);
  c.logicalW = w;
  c.logicalH = h;
  return c;
}

function rr(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.roundRect(x, y, w, h, r);
}

/* ------------------------------------------------------------------ */
/* Chicken                                                            */
/* ------------------------------------------------------------------ */

const RED = '#ec4531';
const RED_DARK = '#b02a1c';
const LEG = '#ff9d1b';

function drawComb(ctx, bumps) {
  ctx.fillStyle = RED;
  ctx.strokeStyle = RED_DARK;
  ctx.lineWidth = 2.4;
  for (const [x, y, r] of bumps) {
    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();
  }
  // little glossy dot on the middle bump
  const [mx, my, mr] = bumps[1];
  ctx.fillStyle = 'rgba(255,255,255,0.35)';
  ctx.beginPath();
  ctx.arc(mx - mr * 0.3, my - mr * 0.35, mr * 0.28, 0, Math.PI * 2);
  ctx.fill();
}

function drawLeg(ctx, x, topY, footY, spread = 1) {
  ctx.strokeStyle = LEG;
  ctx.lineCap = 'round';
  ctx.lineWidth = 6;
  ctx.beginPath();
  ctx.moveTo(x, topY);
  ctx.lineTo(x, footY);
  ctx.stroke();
  // three splayed toes + a tiny heel spur
  ctx.lineWidth = 4.6;
  for (const dx of [-8 * spread, 1, 9 * spread]) {
    ctx.beginPath();
    ctx.moveTo(x, footY - 2);
    ctx.lineTo(x + dx, footY + 6);
    ctx.stroke();
  }
  ctx.lineWidth = 3.6;
  ctx.beginPath();
  ctx.moveTo(x, footY - 3);
  ctx.lineTo(x - 5 * spread, footY);
  ctx.stroke();
}

/** The plump body silhouette, one smooth closed curve (faces right). */
function chickenBodyPath(ctx) {
  ctx.beginPath();
  ctx.moveTo(72, 38);
  ctx.bezierCurveTo(92, 38, 108, 44, 114, 56);   // crown → brow
  ctx.bezierCurveTo(119, 66, 121, 74, 120, 84);  // face front
  ctx.bezierCurveTo(119, 100, 112, 114, 102, 122); // chest
  ctx.bezierCurveTo(96, 129, 82, 133, 68, 132);  // belly
  ctx.bezierCurveTo(54, 131, 42, 126, 36, 116);  // rump
  ctx.bezierCurveTo(29, 105, 26, 93, 27, 82);    // back-left
  ctx.bezierCurveTo(28, 68, 33, 56, 42, 50);     // shoulder
  ctx.bezierCurveTo(51, 43, 60, 38, 72, 38);
  ctx.closePath();
}

function drawChickenBase(ctx, { blink = false, hop = false } = {}) {
  // legs — behind the body, sticking out below the belly
  if (hop) {
    // tucked mid-air: short bent stubs
    ctx.strokeStyle = LEG;
    ctx.lineCap = 'round';
    ctx.lineWidth = 6;
    for (const [x1, y1, x2, y2, x3, y3] of [
      [58, 122, 51, 130, 58, 135],
      [84, 120, 78, 129, 86, 133],
    ]) {
      ctx.beginPath();
      ctx.moveTo(x1, y1);
      ctx.quadraticCurveTo(x2, y2, x3, y3);
      ctx.stroke();
    }
  } else {
    drawLeg(ctx, 60, 122, 141);
    drawLeg(ctx, 84, 120, 141);
  }

  // tail — fluffy scallops poking out of the rear
  ctx.fillStyle = '#ffffff';
  ctx.strokeStyle = OUTLINE;
  ctx.lineWidth = 2.6;
  for (const [x, y, r] of [[29, 57, 12], [22, 72, 10], [27, 87, 8.5]]) {
    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();
  }

  // comb — softer, slightly smaller bumps peeking over the crown
  drawComb(ctx, [[66, 34, 8.2], [80, 28.5, 10.5], [94, 35.5, 8.2]]);

  // body: soft radial fill + one clean outline
  const grad = ctx.createRadialGradient(86, 60, 10, 76, 84, 84);
  grad.addColorStop(0, '#ffffff');
  grad.addColorStop(0.55, '#f7f8fb');
  grad.addColorStop(1, '#e3e6ee');
  ctx.fillStyle = grad;
  ctx.strokeStyle = OUTLINE;
  ctx.lineWidth = 3.2;
  chickenBodyPath(ctx);
  ctx.fill();
  ctx.stroke();

  // interior shading, clipped to the body
  ctx.save();
  chickenBodyPath(ctx);
  ctx.clip();
  let sh = ctx.createRadialGradient(58, 124, 6, 58, 118, 74);
  sh.addColorStop(0, 'rgba(122,132,158,0.22)');
  sh.addColorStop(1, 'rgba(122,132,158,0)');
  ctx.fillStyle = sh;
  ctx.fillRect(0, 0, 150, 150);
  sh = ctx.createRadialGradient(92, 52, 2, 92, 54, 40);
  sh.addColorStop(0, 'rgba(255,255,255,0.55)');
  sh.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = sh;
  ctx.fillRect(0, 0, 150, 150);
  ctx.restore();

  // wing — plump teardrop with scalloped feather tips
  ctx.save();
  ctx.translate(57, 91);
  ctx.rotate(hop ? -0.62 : -0.18);
  ctx.scale(0.88, 0.88);
  ctx.fillStyle = '#eaedf4';
  ctx.strokeStyle = OUTLINE;
  ctx.lineWidth = 2.8;
  ctx.beginPath();
  ctx.moveTo(-24, -2);
  ctx.bezierCurveTo(-20, -14, -4, -18, 8, -14);
  ctx.bezierCurveTo(20, -10, 24, -2, 20, 5);
  ctx.bezierCurveTo(16, 12, 4, 16, -6, 14);
  ctx.bezierCurveTo(-16, 12, -26, 8, -24, -2);
  ctx.closePath();
  ctx.fill();
  ctx.stroke();
  // feather grooves along the trailing edge
  ctx.strokeStyle = 'rgba(58,61,69,0.38)';
  ctx.lineWidth = 2;
  for (const [sx, sy] of [[-19, 2], [-12, 7]]) {
    ctx.beginPath();
    ctx.moveTo(sx, sy - 8);
    ctx.quadraticCurveTo(sx - 4, sy, sx + 2, sy + 6);
    ctx.stroke();
  }
  // soft top light on the wing
  ctx.strokeStyle = 'rgba(255,255,255,0.7)';
  ctx.lineWidth = 2.4;
  ctx.beginPath();
  ctx.moveTo(-14, -10);
  ctx.quadraticCurveTo(2, -15, 14, -9);
  ctx.stroke();
  ctx.restore();

  // beak — small, rounded, tip turned up in a little smile
  ctx.fillStyle = '#ffae26';
  ctx.strokeStyle = '#c07a12';
  ctx.lineWidth = 2.4;
  ctx.beginPath();
  ctx.moveTo(111, 71);
  ctx.quadraticCurveTo(133, 71, 135, 77);
  ctx.quadraticCurveTo(135, 81.5, 124, 83.5);
  ctx.quadraticCurveTo(114, 85, 111, 83.5);
  ctx.closePath();
  ctx.fill();
  ctx.stroke();
  ctx.fillStyle = '#e78c17';
  ctx.strokeStyle = '#c07a12';
  ctx.lineWidth = 1.8;
  ctx.beginPath();
  ctx.moveTo(113, 85);
  ctx.quadraticCurveTo(124, 87.5, 129, 85.5);
  ctx.quadraticCurveTo(125, 92, 115, 90.5);
  ctx.quadraticCurveTo(112, 88.5, 113, 85);
  ctx.closePath();
  ctx.fill();
  ctx.stroke();
  // beak gloss
  ctx.strokeStyle = 'rgba(255,255,255,0.6)';
  ctx.lineWidth = 1.8;
  ctx.beginPath();
  ctx.moveTo(115, 74.5);
  ctx.quadraticCurveTo(125, 74.5, 129, 77);
  ctx.stroke();

  // wattle — smaller teardrop tucked under the beak
  ctx.fillStyle = RED;
  ctx.strokeStyle = RED_DARK;
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(106, 90);
  ctx.bezierCurveTo(103, 98, 105, 105, 110, 106);
  ctx.bezierCurveTo(115, 107, 117, 100, 115.5, 94);
  ctx.quadraticCurveTo(114, 89, 106, 90);
  ctx.closePath();
  ctx.fill();
  ctx.stroke();

  // eye — big, tall and glossy (the cute is in the sparkle)
  if (blink) {
    ctx.strokeStyle = '#26282e';
    ctx.lineCap = 'round';
    ctx.lineWidth = 3.6;
    ctx.beginPath();
    ctx.arc(96, 64, 8.5, 0.15 * Math.PI, 0.85 * Math.PI);
    ctx.stroke();
  } else {
    ctx.fillStyle = '#26282e';
    ctx.beginPath();
    ctx.ellipse(96, 66, 9.6, 10.8, 0, 0, Math.PI * 2);
    ctx.fill();
    // big soft catchlight + two small sparkles
    ctx.fillStyle = '#ffffff';
    ctx.beginPath();
    ctx.arc(99.6, 61.8, 4.2, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = 'rgba(255,255,255,0.75)';
    ctx.beginPath();
    ctx.arc(92.6, 70.5, 2.2, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = 'rgba(255,255,255,0.45)';
    ctx.beginPath();
    ctx.arc(99.5, 71.5, 1.3, 0, Math.PI * 2);
    ctx.fill();
  }

  // cheek blush — soft and clearly visible
  ctx.fillStyle = 'rgba(255,132,112,0.42)';
  ctx.beginPath();
  ctx.ellipse(108, 81, 8, 5.5, -0.15, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = 'rgba(255,132,112,0.2)';
  ctx.beginPath();
  ctx.ellipse(108, 81, 10.5, 7.2, -0.15, 0, Math.PI * 2);
  ctx.fill();
}

const bakeChicken = (opts) => bake(150, 150, (ctx) => drawChickenBase(ctx, opts));

const bakeChickenDead = () =>
  bake(180, 96, (ctx) => {
    const bx = 90, by = 52;
    // feet first — sticking out from under the pancake, kicked up
    ctx.save();
    ctx.translate(bx - 40, by + 12);
    ctx.rotate(-0.5);
    drawLeg(ctx, 0, -6, 16, 0.8);
    ctx.restore();
    ctx.save();
    ctx.translate(bx - 20, by + 16);
    ctx.rotate(-0.15);
    drawLeg(ctx, 0, -6, 14, 0.8);
    ctx.restore();

    // a couple of loose feathers by the body
    ctx.fillStyle = '#ffffff';
    ctx.strokeStyle = 'rgba(58,61,69,0.5)';
    ctx.lineWidth = 1.6;
    for (const [x, y, r, a] of [[20, 30, 6, 0.4], [160, 40, 5, -0.5], [40, 78, 5, 1.1]]) {
      ctx.save();
      ctx.translate(x, y);
      ctx.rotate(a);
      ctx.beginPath();
      ctx.ellipse(0, 0, r + 3, r - 1, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();
      ctx.restore();
    }

    // comb splayed to the side
    drawComb(ctx, [[bx + 22, by - 20, 6.5], [bx + 34, by - 23, 8.5], [bx + 45, by - 18, 6.5]]);

    // flattened body
    const grad = ctx.createRadialGradient(bx - 10, by - 8, 4, bx, by, 74);
    grad.addColorStop(0, '#ffffff');
    grad.addColorStop(1, '#e0e3eb');
    ctx.fillStyle = grad;
    ctx.strokeStyle = OUTLINE;
    ctx.lineWidth = 3.2;
    ctx.beginPath();
    ctx.ellipse(bx, by, 66, 21, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();

    // flattened wing
    ctx.fillStyle = '#f1f3f8';
    ctx.strokeStyle = OUTLINE;
    ctx.lineWidth = 2.4;
    ctx.beginPath();
    ctx.ellipse(bx - 24, by + 2, 26, 10, -0.08, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();
    ctx.strokeStyle = 'rgba(58,61,69,0.35)';
    ctx.lineWidth = 1.8;
    ctx.beginPath();
    ctx.moveTo(bx - 44, by + 2);
    ctx.quadraticCurveTo(bx - 24, by + 8, bx - 4, by + 3);
    ctx.stroke();

    // beak squashed sideways + wattle
    ctx.fillStyle = '#ffae26';
    ctx.strokeStyle = '#c07a12';
    ctx.lineWidth = 2.2;
    ctx.beginPath();
    ctx.moveTo(bx + 54, by - 8);
    ctx.quadraticCurveTo(bx + 74, by - 5, bx + 74, by - 1);
    ctx.quadraticCurveTo(bx + 74, by + 3, bx + 55, by + 4);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.fillStyle = RED;
    ctx.strokeStyle = RED_DARK;
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.ellipse(bx + 50, by + 10, 6, 4.5, 0.3, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();

    // X eye
    ctx.strokeStyle = '#26282e';
    ctx.lineCap = 'round';
    ctx.lineWidth = 3.6;
    for (const [a, b] of [[[-6, -6], [6, 6]], [[6, -6], [-6, 6]]]) {
      ctx.beginPath();
      ctx.moveTo(bx + 34 + a[0], by - 4 + a[1]);
      ctx.lineTo(bx + 34 + b[0], by - 4 + b[1]);
      ctx.stroke();
    }
  });

/* ------------------------------------------------------------------ */
/* Road furniture                                                     */
/* ------------------------------------------------------------------ */

const bakeManhole = () =>
  bake(112, 112, (ctx) => {
    const c = 56, R = 52;
    // fake emboss: light top rim / dark bottom rim behind the base disc
    ctx.fillStyle = '#666d79';
    ctx.beginPath();
    ctx.arc(c, c - 1.5, R, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = '#2e3138';
    ctx.beginPath();
    ctx.arc(c, c + 1.5, R, 0, Math.PI * 2);
    ctx.fill();
    // base disc
    ctx.fillStyle = '#4a4f58';
    ctx.beginPath();
    ctx.arc(c, c, R - 1.5, 0, Math.PI * 2);
    ctx.fill();

    // radial hatch ticks in the outer band
    ctx.strokeStyle = '#3d414a';
    ctx.lineWidth = 2.4;
    for (let i = 0; i < 30; i++) {
      const a = (i / 30) * Math.PI * 2;
      ctx.beginPath();
      ctx.moveTo(c + Math.cos(a) * 42, c + Math.sin(a) * 42);
      ctx.lineTo(c + Math.cos(a) * 48.5, c + Math.sin(a) * 48.5);
      ctx.stroke();
    }

    // groove rings (dark + light pair = engraved)
    for (const r of [41, 33]) {
      ctx.strokeStyle = '#383c44';
      ctx.lineWidth = 2.6;
      ctx.beginPath();
      ctx.arc(c, c, r, 0, Math.PI * 2);
      ctx.stroke();
      ctx.strokeStyle = 'rgba(122,129,141,0.8)';
      ctx.lineWidth = 1.2;
      ctx.beginPath();
      ctx.arc(c, c + 1.4, r, 0, Math.PI * 2);
      ctx.stroke();
    }

    // bolts between the grooves
    for (let i = 0; i < 8; i++) {
      const a = (i / 8) * Math.PI * 2 + Math.PI / 8;
      const x = c + Math.cos(a) * 37, y = c + Math.sin(a) * 37;
      ctx.fillStyle = '#343841';
      ctx.beginPath();
      ctx.arc(x, y, 2.6, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = 'rgba(140,147,158,0.55)';
      ctx.beginPath();
      ctx.arc(x, y + 1, 2.6, Math.PI * 0.15, Math.PI * 0.85);
      ctx.fill();
    }

    // flat center plate (multiplier text goes on top of this, composited later)
    ctx.fillStyle = '#454a53';
    ctx.beginPath();
    ctx.arc(c, c, 30, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = 'rgba(30,33,39,0.6)';
    ctx.lineWidth = 1.4;
    ctx.beginPath();
    ctx.arc(c, c, 30, 0, Math.PI * 2);
    ctx.stroke();
  });

const bakeCoin = () =>
  bake(96, 96, (ctx) => {
    const c = 48;
    // face
    const g = ctx.createRadialGradient(c - 14, c - 16, 6, c, c, 46);
    g.addColorStop(0, '#ffe98f');
    g.addColorStop(0.55, '#ffd75e');
    g.addColorStop(1, '#f0a51d');
    ctx.fillStyle = g;
    ctx.strokeStyle = '#c47f16';
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.arc(c, c, 44, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();
    // rim ring
    ctx.strokeStyle = '#d4881c';
    ctx.lineWidth = 4.5;
    ctx.beginPath();
    ctx.arc(c, c, 36.5, 0, Math.PI * 2);
    ctx.stroke();

    // stamped chicken head silhouette
    ctx.save();
    ctx.translate(c, c + 3);
    // drop shading first (offset dark), then the cream emboss
    for (const [off, color] of [[2, 'rgba(160,100,10,0.55)'], [0, '#fff3cc']]) {
      ctx.save();
      ctx.translate(0, off);
      ctx.fillStyle = color;
      // head
      ctx.beginPath();
      ctx.arc(0, 0, 17, 0, Math.PI * 2);
      ctx.fill();
      // comb
      for (const [dx, r] of [[-9, 5.5], [0, 7], [9, 5.5]]) {
        ctx.beginPath();
        ctx.arc(dx, -16, r, 0, Math.PI * 2);
        ctx.fill();
      }
      // beak
      ctx.beginPath();
      ctx.moveTo(15, -4);
      ctx.lineTo(27, 1);
      ctx.lineTo(15, 6);
      ctx.closePath();
      ctx.fill();
      ctx.restore();
    }
    // eye punched out of the emboss
    ctx.fillStyle = '#e5a52c';
    ctx.beginPath();
    ctx.arc(5, -4, 2.8, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();

    // specular arc
    ctx.strokeStyle = 'rgba(255,255,255,0.55)';
    ctx.lineWidth = 5;
    ctx.beginPath();
    ctx.arc(c, c, 39, Math.PI * 1.08, Math.PI * 1.55);
    ctx.stroke();
  });

const bakeBarrier = () =>
  bake(132, 66, (ctx) => {
    // posts
    for (const x of [16, 102]) {
      ctx.fillStyle = '#3f434b';
      ctx.strokeStyle = '#26282e';
      ctx.lineWidth = 2;
      rr(ctx, x, 24, 14, 36, 4);
      ctx.fill();
      ctx.stroke();
      ctx.fillStyle = 'rgba(255,255,255,0.18)';
      rr(ctx, x + 2, 26, 10, 4, 2);
      ctx.fill();
    }
    // striped bar
    ctx.save();
    rr(ctx, 4, 8, 124, 28, 14);
    ctx.clip();
    ctx.fillStyle = '#ffcc33';
    ctx.fillRect(4, 8, 124, 28);
    ctx.fillStyle = '#2b2b31';
    ctx.save();
    ctx.translate(0, 22);
    ctx.rotate(-Math.PI / 4);
    for (let x = -60; x < 160; x += 30) ctx.fillRect(x, -80, 15, 160);
    ctx.restore();
    // top sheen
    ctx.fillStyle = 'rgba(255,255,255,0.22)';
    ctx.fillRect(4, 9, 124, 5);
    ctx.restore();
    ctx.strokeStyle = '#26282e';
    ctx.lineWidth = 2.6;
    rr(ctx, 4, 8, 124, 28, 14);
    ctx.stroke();
  });

/* ------------------------------------------------------------------ */
/* Vehicles (top view, driving DOWN — front bumper at the bottom)     */
/* ------------------------------------------------------------------ */

function shade(hex, f) {
  const n = parseInt(hex.slice(1), 16);
  const ch = (s) => Math.max(0, Math.min(255, Math.round(((n >> s) & 255) * f)));
  return `#${((ch(16) << 16) | (ch(8) << 8) | ch(0)).toString(16).padStart(6, '0')}`;
}

function bakeCar({ body, taxi = false, stripe = null }) {
  return bake(96, 156, (ctx) => {
    const L = 8, T = 8, W = 80, H = 140;
    // wheels peeking out
    ctx.fillStyle = '#26282e';
    for (const [x, y] of [[2, 26], [84, 26], [2, 100], [84, 100]]) {
      rr(ctx, x, y, 10, 28, 5);
      ctx.fill();
    }
    // body — chunky rounded shell
    const g = ctx.createLinearGradient(L, 0, L + W, 0);
    g.addColorStop(0, shade(body, 0.86));
    g.addColorStop(0.3, shade(body, 1.04));
    g.addColorStop(0.7, body);
    g.addColorStop(1, shade(body, 0.76));
    ctx.fillStyle = g;
    ctx.strokeStyle = shade(body, 0.48);
    ctx.lineWidth = 3.2;
    rr(ctx, L, T, W, H, 26);
    ctx.fill();
    ctx.stroke();

    // long soft highlight down the left flank
    ctx.fillStyle = 'rgba(255,255,255,0.16)';
    rr(ctx, L + 5, T + 14, 9, H - 28, 5);
    ctx.fill();

    // hood scoop line (front) + trunk line (rear)
    ctx.strokeStyle = shade(body, 0.6);
    ctx.lineWidth = 2.2;
    ctx.beginPath();
    ctx.moveTo(L + 12, T + 118);
    ctx.quadraticCurveTo(L + W / 2, T + 126, L + W - 12, T + 118);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(L + 13, T + 20);
    ctx.quadraticCurveTo(L + W / 2, T + 15, L + W - 13, T + 20);
    ctx.stroke();

    // side mirrors
    ctx.fillStyle = shade(body, 0.62);
    for (const x of [L - 7, L + W - 1]) {
      rr(ctx, x, T + 82, 8, 9, 3);
      ctx.fill();
    }

    // glass: big friendly windows with double glare
    const glass = (y, h, wideBottom) => {
      const i1 = wideBottom ? 13 : 9, i2 = wideBottom ? 9 : 13;
      ctx.fillStyle = '#33465c';
      ctx.strokeStyle = shade(body, 0.55);
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(L + i1, y);
      ctx.lineTo(L + W - i1, y);
      ctx.quadraticCurveTo(L + W - i2 + 2, y + h * 0.55, L + W - i2, y + h);
      ctx.lineTo(L + i2, y + h);
      ctx.quadraticCurveTo(L + i2 - 2, y + h * 0.55, L + i1, y);
      ctx.closePath();
      ctx.fill();
      ctx.stroke();
      ctx.save();
      ctx.clip();
      ctx.fillStyle = 'rgba(255,255,255,0.28)';
      ctx.save();
      ctx.translate(L + 22, y + h / 2);
      ctx.rotate(-0.55);
      ctx.fillRect(0, -h * 1.5, 12, h * 3);
      ctx.fillRect(19, -h * 1.5, 6, h * 3);
      ctx.restore();
      ctx.restore();
    };
    if (stripe) {
      // racing stripes sit on the paint, under the glass and roof
      ctx.fillStyle = stripe;
      ctx.globalAlpha = 0.9;
      rr(ctx, L + W / 2 - 10, T + 5, 8, H - 10, 4);
      ctx.fill();
      rr(ctx, L + W / 2 + 2, T + 5, 8, H - 10, 4);
      ctx.fill();
      ctx.globalAlpha = 1;
    }

    glass(T + 88, 28, true);  // windshield (front, lower)
    glass(T + 28, 20, false); // rear window

    // roof panel
    ctx.fillStyle = shade(body, 0.95);
    ctx.strokeStyle = shade(body, 0.68);
    ctx.lineWidth = 1.8;
    rr(ctx, L + 12, T + 52, W - 24, 32, 10);
    ctx.fill();
    ctx.stroke();
    if (taxi) {
      // checker band along the roof sides + lit roof sign
      ctx.fillStyle = '#2b2b31';
      const s = 6.5;
      for (let i = 0; i < (W - 24) / s; i++) {
        for (let j = 0; j < 2; j++) {
          if ((i + j) % 2 === 0) ctx.fillRect(L + 12 + i * s, T + 52 + j * s, s, s);
        }
      }
      ctx.fillStyle = '#fff6d8';
      ctx.strokeStyle = '#2b2b31';
      ctx.lineWidth = 1.8;
      rr(ctx, L + W / 2 - 16, T + 64, 32, 13, 4);
      ctx.fill();
      ctx.stroke();
      ctx.fillStyle = '#2b2b31';
      ctx.font = '800 9px Arial, sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('TAXI', L + W / 2, T + 71);
    }

    // front bumper shadow + headlights (bottom = front)
    ctx.strokeStyle = shade(body, 0.55);
    ctx.lineWidth = 2.4;
    ctx.beginPath();
    ctx.moveTo(L + 10, T + H - 12);
    ctx.lineTo(L + W - 10, T + H - 12);
    ctx.stroke();
    ctx.fillStyle = '#fff3c1';
    ctx.strokeStyle = '#d8b24a';
    ctx.lineWidth = 1.6;
    for (const x of [L + 7, L + W - 25]) {
      rr(ctx, x, T + H - 10, 18, 7, 3.5);
      ctx.fill();
      ctx.stroke();
    }
    // taillights
    ctx.fillStyle = '#ff5148';
    for (const x of [L + 7, L + W - 23]) {
      rr(ctx, x, T + 2.5, 16, 6, 3);
      ctx.fill();
    }
  });
}

/** Sponsor van: dark body with the TIME4BETS wordmark on the roof. */
const bakeAdVan = () =>
  bake(100, 190, (ctx) => {
    const L = 8, T = 8, W = 84, H = 174, body = '#0d1a14';
    ctx.fillStyle = '#26282e';
    for (const [x, y] of [[2, 30], [90, 30], [2, 130], [90, 130]]) {
      rr(ctx, x, y, 8, 28, 4);
      ctx.fill();
    }
    // boxy body
    const g = ctx.createLinearGradient(L, 0, L + W, 0);
    g.addColorStop(0, '#0a1410');
    g.addColorStop(0.3, '#12241b');
    g.addColorStop(0.7, body);
    g.addColorStop(1, '#081109');
    ctx.fillStyle = g;
    ctx.strokeStyle = '#050a07';
    ctx.lineWidth = 3;
    rr(ctx, L, T, W, H, 14);
    ctx.fill();
    ctx.stroke();
    // green brand trim
    ctx.strokeStyle = '#2fbf71';
    ctx.lineWidth = 2;
    rr(ctx, L + 4, T + 4, W - 8, H - 8, 10);
    ctx.stroke();

    // windshield at the front (bottom)
    ctx.fillStyle = '#33465c';
    ctx.strokeStyle = '#050a07';
    ctx.lineWidth = 2;
    rr(ctx, L + 10, T + H - 34, W - 20, 22, 6);
    ctx.fill();
    ctx.stroke();
    ctx.save();
    rr(ctx, L + 10, T + H - 34, W - 20, 22, 6);
    ctx.clip();
    ctx.fillStyle = 'rgba(255,255,255,0.25)';
    ctx.save();
    ctx.translate(L + 24, T + H - 23);
    ctx.rotate(-0.55);
    ctx.fillRect(0, -26, 10, 52);
    ctx.fillRect(16, -26, 5, 52);
    ctx.restore();
    ctx.restore();

    // roof billboard: TIME4BETS along the length
    ctx.save();
    ctx.translate(L + W / 2, T + (H - 40) / 2);
    ctx.rotate(Math.PI / 2);
    ctx.font = 'italic 900 21px "Arial Black", Arial, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    const wordGrad = ctx.createLinearGradient(-60, 0, 60, 0);
    wordGrad.addColorStop(0, '#7df0b2');
    wordGrad.addColorStop(1, '#1f9d5c');
    ctx.fillStyle = 'rgba(0,0,0,0.55)';
    ctx.fillText('TIME4BETS', 0, 2);
    ctx.fillStyle = wordGrad;
    ctx.fillText('TIME4BETS', 0, 0);
    // the "4" pops in white, like the logo
    ctx.fillStyle = '#ffffff';
    ctx.fillText('4', -1, 0.5);
    ctx.restore();

    // lights
    ctx.fillStyle = '#fff3c1';
    for (const x of [L + 7, L + W - 25]) {
      rr(ctx, x, T + H - 9, 18, 6, 3);
      ctx.fill();
    }
    ctx.fillStyle = '#ff5148';
    for (const x of [L + 7, L + W - 23]) {
      rr(ctx, x, T + 3, 16, 5, 2.5);
      ctx.fill();
    }
  });

const bakeFireTruck = () =>
  bake(108, 216, (ctx) => {
    const L = 9, T = 8, W = 90, H = 200, body = '#e8483a';
    ctx.fillStyle = '#26282e';
    for (const [x, y] of [[2, 32], [96, 32], [2, 98], [96, 98], [2, 152], [96, 152]]) {
      rr(ctx, x, y, 10, 30, 5);
      ctx.fill();
    }
    const g = ctx.createLinearGradient(L, 0, L + W, 0);
    g.addColorStop(0, shade(body, 0.84));
    g.addColorStop(0.3, shade(body, 1.05));
    g.addColorStop(0.7, body);
    g.addColorStop(1, shade(body, 0.74));
    ctx.fillStyle = g;
    ctx.strokeStyle = shade(body, 0.45);
    ctx.lineWidth = 3.2;
    rr(ctx, L, T, W, H, 18);
    ctx.fill();
    ctx.stroke();

    // flank highlight
    ctx.fillStyle = 'rgba(255,255,255,0.14)';
    rr(ctx, L + 5, T + 14, 9, H - 28, 5);
    ctx.fill();

    // white mid-band between tank and cab
    ctx.fillStyle = '#f4f5f8';
    ctx.strokeStyle = shade(body, 0.6);
    ctx.lineWidth = 1.8;
    rr(ctx, L + 3, T + 122, W - 6, 16, 5);
    ctx.fill();
    ctx.stroke();

    // cab windshield (front/bottom) with glare
    ctx.fillStyle = '#33465c';
    ctx.strokeStyle = shade(body, 0.55);
    ctx.lineWidth = 2;
    rr(ctx, L + 11, T + 146, W - 22, 26, 7);
    ctx.fill();
    ctx.stroke();
    ctx.save();
    rr(ctx, L + 11, T + 146, W - 22, 26, 7);
    ctx.clip();
    ctx.fillStyle = 'rgba(255,255,255,0.28)';
    ctx.save();
    ctx.translate(L + 26, T + 159);
    ctx.rotate(-0.55);
    ctx.fillRect(0, -34, 12, 68);
    ctx.fillRect(19, -34, 6, 68);
    ctx.restore();
    ctx.restore();

    // roof lightbar between windshield and band (blue + red)
    ctx.fillStyle = '#3e6fd8';
    rr(ctx, L + 16, T + 176, 22, 10, 4);
    ctx.fill();
    ctx.fillStyle = '#ff5148';
    rr(ctx, L + W - 38, T + 176, 22, 10, 4);
    ctx.fill();
    ctx.fillStyle = 'rgba(255,255,255,0.5)';
    rr(ctx, L + 18, T + 177.5, 8, 3, 1.5);
    ctx.fill();
    rr(ctx, L + W - 36, T + 177.5, 8, 3, 1.5);
    ctx.fill();

    // ladder along the tank: rails + rungs + turntable
    ctx.fillStyle = '#9aa2ad';
    ctx.strokeStyle = '#646b76';
    ctx.lineWidth = 2;
    rr(ctx, L + W / 2 - 14, T + 12, 28, 108, 7);
    ctx.fill();
    ctx.stroke();
    ctx.strokeStyle = '#646b76';
    ctx.lineWidth = 3.2;
    for (const x of [L + W / 2 - 9, L + W / 2 + 9]) {
      ctx.beginPath();
      ctx.moveTo(x, T + 18);
      ctx.lineTo(x, T + 114);
      ctx.stroke();
    }
    ctx.lineWidth = 2.6;
    for (let y = T + 25; y <= T + 110; y += 11) {
      ctx.beginPath();
      ctx.moveTo(L + W / 2 - 9, y);
      ctx.lineTo(L + W / 2 + 9, y);
      ctx.stroke();
    }
    // ladder tip highlight
    ctx.fillStyle = 'rgba(255,255,255,0.35)';
    rr(ctx, L + W / 2 - 12, T + 14, 24, 5, 2.5);
    ctx.fill();
    // turntable
    ctx.fillStyle = '#b7bdc7';
    ctx.beginPath();
    ctx.arc(L + W / 2, T + 124, 13, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = '#646b76';
    ctx.lineWidth = 2.4;
    ctx.stroke();
    ctx.fillStyle = '#8d939e';
    ctx.beginPath();
    ctx.arc(L + W / 2, T + 124, 5.5, 0, Math.PI * 2);
    ctx.fill();

    // bumpers + headlights
    ctx.fillStyle = '#ffcc33';
    rr(ctx, L + 5, T + H - 8, W - 10, 7, 3.5);
    ctx.fill();
    rr(ctx, L + 5, T + 1.5, W - 10, 6, 3);
    ctx.fill();
    ctx.fillStyle = '#fff3c1';
    ctx.strokeStyle = '#d8b24a';
    ctx.lineWidth = 1.6;
    for (const x of [L + 8, L + W - 28]) {
      rr(ctx, x, T + H - 18, 20, 8, 4);
      ctx.fill();
      ctx.stroke();
    }
  });

/* ------------------------------------------------------------------ */
/* Ground                                                             */
/* ------------------------------------------------------------------ */

const bakeLaneStrip = () =>
  bake(LANE_W, VIEW_H, (ctx, w, h) => {
    ctx.fillStyle = '#5a5e66';
    ctx.fillRect(0, 0, w, h);
    // baked asphalt noise (deterministic-ish; randomness at bake time is fine)
    for (let i = 0; i < 420; i++) {
      const x = Math.random() * w, y = Math.random() * h;
      const l = Math.random();
      ctx.fillStyle = l > 0.5 ? 'rgba(255,255,255,0.045)' : 'rgba(0,0,0,0.06)';
      ctx.fillRect(x, y, 1 + Math.random() * 1.6, 1 + Math.random() * 1.6);
    }
    // a few faint larger patches
    for (let i = 0; i < 4; i++) {
      ctx.fillStyle = 'rgba(0,0,0,0.035)';
      ctx.beginPath();
      ctx.ellipse(Math.random() * w, Math.random() * h, 18 + Math.random() * 26, 12 + Math.random() * 18, Math.random() * 3, 0, Math.PI * 2);
      ctx.fill();
    }
    // subtle vertical vignette
    const vg = ctx.createLinearGradient(0, 0, 0, h);
    vg.addColorStop(0, 'rgba(0,0,0,0.10)');
    vg.addColorStop(0.2, 'rgba(0,0,0,0)');
    vg.addColorStop(0.8, 'rgba(0,0,0,0)');
    vg.addColorStop(1, 'rgba(0,0,0,0.12)');
    ctx.fillStyle = vg;
    ctx.fillRect(0, 0, w, h);
    // right-edge dashed separator
    ctx.fillStyle = 'rgba(236,238,242,0.92)';
    for (let y = -20; y < h; y += 68) {
      rr(ctx, w - 3.5, y, 6, 42, 3);
      ctx.fill();
    }
  });

function bakeSidewalk(side) {
  return bake(SIDEWALK_W, VIEW_H, (ctx, w, h) => {
    const grassW = 62;
    const grassX = side === 'left' ? 0 : w - grassW;
    const paveX = side === 'left' ? grassW : 0;
    const paveW = w - grassW;
    const curbX = side === 'left' ? w - 16 : 0;

    // pavement
    ctx.fillStyle = '#b9bec6';
    ctx.fillRect(paveX, 0, paveW, h);
    ctx.strokeStyle = 'rgba(122,128,138,0.55)';
    ctx.lineWidth = 1.6;
    for (let y = 0; y <= h; y += 66) {
      ctx.beginPath();
      ctx.moveTo(paveX, y);
      ctx.lineTo(paveX + paveW, y);
      ctx.stroke();
    }
    ctx.beginPath();
    ctx.moveTo(paveX + paveW / 2, 0);
    ctx.lineTo(paveX + paveW / 2, h);
    ctx.stroke();
    // pavement speckle
    for (let i = 0; i < 240; i++) {
      ctx.fillStyle = Math.random() > 0.5 ? 'rgba(255,255,255,0.10)' : 'rgba(70,75,85,0.07)';
      ctx.fillRect(paveX + Math.random() * paveW, Math.random() * h, 1.4, 1.4);
    }

    // grass strip with a wobbly inner edge
    ctx.fillStyle = '#57bd4b';
    ctx.fillRect(grassX, 0, grassW, h);
    ctx.fillStyle = '#57bd4b';
    ctx.beginPath();
    const edgeX = side === 'left' ? grassW : grassX;
    ctx.moveTo(edgeX, 0);
    for (let y = 0; y <= h; y += 24) {
      const bump = Math.sin(y * 0.09) * 5 + (Math.random() * 3 - 1.5);
      ctx.lineTo(edgeX + (side === 'left' ? bump : -bump), y);
    }
    ctx.lineTo(edgeX + (side === 'left' ? -30 : 30), h);
    ctx.lineTo(edgeX + (side === 'left' ? -30 : 30), 0);
    ctx.closePath();
    ctx.fill();
    // grass texture blobs
    for (let i = 0; i < 90; i++) {
      ctx.fillStyle = Math.random() > 0.5 ? 'rgba(38,130,44,0.35)' : 'rgba(150,225,120,0.3)';
      ctx.beginPath();
      ctx.ellipse(grassX + Math.random() * grassW, Math.random() * h, 2.5, 1.6, Math.random() * 3, 0, Math.PI * 2);
      ctx.fill();
    }

    // curb facing the road
    ctx.fillStyle = '#cdd2d9';
    ctx.fillRect(curbX, 0, 16, h);
    ctx.fillStyle = 'rgba(60,64,72,0.35)';
    ctx.fillRect(side === 'left' ? curbX + 13 : curbX, 0, 3, h);
    ctx.strokeStyle = 'rgba(122,128,138,0.7)';
    ctx.lineWidth = 1.4;
    for (let y = 33; y < h; y += 66) {
      ctx.beginPath();
      ctx.moveTo(curbX, y);
      ctx.lineTo(curbX + 16, y);
      ctx.stroke();
    }
  });
}

const bakeTree = () =>
  bake(150, 175, (ctx) => {
    // trunk
    ctx.fillStyle = '#7a5233';
    ctx.strokeStyle = '#54371f';
    ctx.lineWidth = 2.4;
    rr(ctx, 66, 118, 18, 34, 6);
    ctx.fill();
    ctx.stroke();
    // foliage: shade layer, base layer, highlight blobs
    const blobs = [
      [52, 82, 36], [82, 60, 42], [108, 88, 33], [72, 102, 31],
    ];
    ctx.fillStyle = '#2f8038';
    for (const [x, y, r] of blobs) {
      ctx.beginPath();
      ctx.arc(x + 4, y + 6, r, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.fillStyle = '#3f9f45';
    ctx.strokeStyle = '#2c6e33';
    ctx.lineWidth = 2.6;
    for (const [x, y, r] of blobs) {
      ctx.beginPath();
      ctx.arc(x, y, r, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();
    }
    // re-fill to hide inner strokes, keeping the outer silhouette line
    ctx.fillStyle = '#3f9f45';
    for (const [x, y, r] of blobs) {
      ctx.beginPath();
      ctx.arc(x, y, r - 1.4, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.fillStyle = '#5cc457';
    for (const [x, y, r] of [[66, 66, 17], [94, 54, 14], [50, 90, 12], [88, 84, 11]]) {
      ctx.beginPath();
      ctx.arc(x, y, r, 0, Math.PI * 2);
      ctx.fill();
    }
  });

const bakeLamp = () =>
  bake(90, 210, (ctx) => {
    // base
    ctx.fillStyle = '#3a3d44';
    ctx.strokeStyle = '#26282e';
    ctx.lineWidth = 2;
    rr(ctx, 8, 186, 30, 14, 4);
    ctx.fill();
    ctx.stroke();
    // pole
    const g = ctx.createLinearGradient(16, 0, 30, 0);
    g.addColorStop(0, '#4a4e57');
    g.addColorStop(0.5, '#5a5f69');
    g.addColorStop(1, '#3a3d44');
    ctx.fillStyle = g;
    rr(ctx, 17, 30, 12, 160, 5);
    ctx.fill();
    ctx.stroke();
    // curved arm
    ctx.strokeStyle = '#4a4e57';
    ctx.lineWidth = 9;
    ctx.beginPath();
    ctx.moveTo(23, 36);
    ctx.quadraticCurveTo(24, 18, 62, 20);
    ctx.stroke();
    ctx.strokeStyle = '#26282e';
    ctx.lineWidth = 1.6;
    ctx.beginPath();
    ctx.moveTo(23, 36);
    ctx.quadraticCurveTo(24, 18, 62, 20);
    ctx.stroke();
    // head + warm lens
    ctx.fillStyle = '#3a3d44';
    ctx.strokeStyle = '#26282e';
    ctx.lineWidth = 2;
    rr(ctx, 52, 14, 30, 13, 5);
    ctx.fill();
    ctx.stroke();
    ctx.fillStyle = '#ffd98a';
    rr(ctx, 56, 24, 22, 6, 3);
    ctx.fill();
  });

const bakeLampGlow = () =>
  bake(180, 180, (ctx) => {
    const g = ctx.createRadialGradient(90, 90, 4, 90, 90, 88);
    g.addColorStop(0, 'rgba(255,236,180,0.55)');
    g.addColorStop(0.5, 'rgba(255,226,150,0.18)');
    g.addColorStop(1, 'rgba(255,226,150,0)');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, 180, 180);
  });

const bakeFeather = () =>
  bake(24, 24, (ctx) => {
    ctx.fillStyle = '#ffffff';
    ctx.strokeStyle = 'rgba(49,52,60,0.65)';
    ctx.lineWidth = 1.4;
    ctx.beginPath();
    ctx.moveTo(4, 20);
    ctx.quadraticCurveTo(2, 8, 12, 3);
    ctx.quadraticCurveTo(22, 8, 20, 14);
    ctx.quadraticCurveTo(14, 22, 4, 20);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.strokeStyle = 'rgba(49,52,60,0.4)';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(5, 19);
    ctx.quadraticCurveTo(12, 12, 14, 6);
    ctx.stroke();
  });

/* ------------------------------------------------------------------ */

export function buildSprites() {
  const cars = [
    bakeCar({ body: '#ffc93c', taxi: true }),
    bakeCar({ body: '#4a90e2' }),
    bakeCar({ body: '#58c15c', stripe: '#ffffff' }),
    bakeCar({ body: '#ef5350' }),
    bakeCar({ body: '#9b6dd6' }),
    bakeCar({ body: '#f47fb0', stripe: '#ffe1ef' }),
  ];
  return {
    chicken: bakeChicken({}),
    chickenBlink: bakeChicken({ blink: true }),
    chickenHop: bakeChicken({ hop: true }),
    chickenDead: bakeChickenDead(),
    manhole: bakeManhole(),
    coin: bakeCoin(),
    barrier: bakeBarrier(),
    cars,
    fireTruck: bakeFireTruck(),
    adVan: bakeAdVan(),
    laneStrip: bakeLaneStrip(),
    sidewalkLeft: bakeSidewalk('left'),
    sidewalkRight: bakeSidewalk('right'),
    tree: bakeTree(),
    lamp: bakeLamp(),
    lampGlow: bakeLampGlow(),
    feather: bakeFeather(),
  };
}

/** Draw a baked sprite centered at (cx, cy) with optional transform. */
export function blit(ctx, spr, cx, cy, { sx = 1, sy = 1, rot = 0, alpha = 1 } = {}) {
  ctx.save();
  ctx.globalAlpha *= alpha;
  ctx.translate(cx, cy);
  if (rot) ctx.rotate(rot);
  ctx.scale(sx, sy);
  ctx.drawImage(spr, -spr.logicalW / 2, -spr.logicalH / 2, spr.logicalW, spr.logicalH);
  ctx.restore();
}
